/**
 * The API client.
 *
 * The split this file embodies: **the API owns the data, the browser owns the
 * compute.** Accommodations, versions and periods are shared between operators
 * and cannot live in localStorage — "which version is live" is not a per-browser
 * fact. Quoting stays local, because the engine answers in under a millisecond
 * there and a round trip would be slower than the search.
 */

import type { Programme, Diagnostic } from '@tariff/authoring';
import type { Period } from '@tariff/periods';
import type { ProgrammeDiff } from '@tariff/versions';
import type { Leak, ObservedStep, LadderStep } from '@tariff/generator';
import type { Drift } from '@tariff/periods';

export type VersionState = 'draft' | 'published' | 'archived';

export interface AccommodationDto {
  id: string;
  label: string;
  kind: string;
  /** Values for the park's declared attributes, keyed by attribute id. */
  attributes?: Record<string, string | number | boolean>;
  createdAt: string;
  versions: number;
  published: { id: string; label: string; seasonYear: number } | null;
  /** The season's draft, sent only when that season has nothing published. */
  draft?: { id: string; label: string; seasonYear: number } | null;
}

/** What a park has declared its accommodations can carry: m², amps, a view. */
export interface AttributeDefDto {
  id: string;
  label: string;
  kind: 'number' | 'boolean' | 'enum' | 'text';
  options?: string[];
  unit?: string;
  filterable?: boolean;
  sort?: number;
}

export interface VersionSummaryDto {
  id: string;
  accommodationId: string;
  label: string;
  seasonYear: number;
  state: VersionState;
  parentId: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * The arrivals a published table prices, half-open, as DAY NUMBERS — the
   * engine's unit, which is what the API stores and sends.
   *
   * Several versions of one accommodation may be live at once as long as their
   * windows do not overlap, so "which version is live" is only answerable
   * together with "for which arrival". Null means the window was never stated,
   * which the store reads as covering everything.
   */
  validFrom?: number | null;
  validUntil?: number | null;
  rates: number;
}

/** A terrain: the part of the park a unit sits in. */
export interface UnitGroupDto {
  id: string;
  label: string;
  sort?: number;
}

export interface UnitDto {
  id: string;
  accommodationId: string;
  /** The terrain, where the park is divided into any. */
  groupId?: string | null;
  number?: number | null;
  /** A name of its own, where it has one. */
  label: string;
  /** How the park refers to it: "Berkenlaan 12". Unique within the park. */
  address?: string | null;
  /** Its own name if it has one, else its address. What to put on screen. */
  display?: string;
  sort?: number;
  active?: boolean;
  /** May a guest ask for this unit specifically? */
  selectable?: boolean;
  note?: string | null;
  createdAt?: string;
}

/** Nights a unit cannot be sold. `until` is exclusive, as everywhere here. */
export interface BlockDto {
  id: string;
  unitId: string;
  from: string;
  until: string;
  reason: string;
  note?: string | null;
  createdAt?: string;
}

export interface InquiryDto {
  id: string;
  accommodationId: string;
  arrival: string;
  nights: number;
  party: Record<string, number>;
  /** The same people in channel roles, as the guest picked them. Null on rows
      taken before it was stored; `party` is the card's fold of it. */
  guestParty?: Record<string, number> | null;
  /** What the server computed when it arrived. Null when it could not price it. */
  price?: number | null;
  /** What the browser claimed, kept so a stale widget is visible. */
  quotedPrice?: number | null;
  available?: boolean;
  /** Units free when the guest asked; null where the park does not count them. */
  freeUnits?: number | null;
  pricedBy?: string | null;
  name: string;
  email: string;
  phone?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  /** "bis", "A", "2-hoog". A different door from the bare number. */
  houseNumberAddition?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  licencePlate?: string | null;
  comment?: string | null;
  locale?: string | null;
  channel?: string;
  status?: string;
  remoteAddress?: string | null;
  mailStatus?: string | null;
  /**
   * The guest's own page for this enquiry, token and all.
   *
   * The API returns the built link rather than the bare token, deliberately:
   * an operator needs to open what the guest sees, and the token is the one
   * field that must not spread beyond the single URL that needs it. Null where
   * no site URL is configured.
   */
  url?: string | null;
  createdAt?: string;
}

/** One message the park sent about an enquiry, kept as it was sent. */
export interface InquiryMailDto {
  id: string;
  inquiryId: string;
  /** `operator` or `guest`. */
  recipient: string;
  toAddress: string;
  subject: string;
  bodyText: string;
  bodyHtml?: string | null;
  /** `sent`, or the reason it was not. */
  outcome: string;
  createdAt: string;
}

export interface VersionDto extends Omit<VersionSummaryDto, 'rates'> {
  programme: Programme;
  diagnostics: Diagnostic[];
  ok: boolean;
  drift: Drift[];
  leakage: Leak[];
}

export interface LadderProblemDto {
  stepId: string;
  code: string;
  severity: 'error' | 'warning';
  message: string;
}

export interface LadderProposalDto {
  diff: ProgrammeDiff;
  protected: string[];
  /** Entries that conflict with each other; empty when the ladder is coherent. */
  problems: LadderProblemDto[];
  leakage: Leak[];
  observed: ObservedStep[];
}

export class ApiError extends Error {
  constructor(
    public status: number,
    /** What the API said. Empty when it said nothing a person could read. */
    message: string,
    public detail?: unknown,
    /**
     * The call that failed.
     *
     * Carried rather than baked into `message` so the caller can say "GET /x
     * failed" in the operator's own language. This client has no translation
     * function — it runs outside React and is used by the worker — so it hands
     * over the facts and lets the screen do the wording.
     */
    public request?: { method: string; path: string },
  ) {
    super(message);
  }
}

const BASE = (import.meta.env?.VITE_API ?? '') as string;

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    // The session is a cookie. Same-origin would carry it when the dashboard is
    // served by the API itself, and NOT when it is served from its own address —
    // which is a supported deployment (`PMS_ADMIN_ORIGINS` exists for exactly
    // that). Without this the operator signs in, the cookie is set, and every
    // subsequent request is anonymous.
    credentials: 'include',
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    // The API's error messages are written for a human to read, so surface them
    // rather than a status code. A 422 carries diagnostics worth showing.
    throw new ApiError(res.status, json?.error ?? '', json, { method, path });
  }
  return json as T;
}

/**
 * A condition a stay must meet, and what happens when it does not.
 *
 * `from`/`until` are ISO dates and describe ARRIVALS the rule governs — half-open,
 * so `until` is the first day it no longer applies. Weekdays are 0 = Sunday, the
 * API's own numbering.
 */
export interface StayRuleDto {
  id: string;
  /** Null governs every type in the park. */
  accommodationId?: string | null;
  from: string;
  until: string;
  arriveOn?: number[] | null;
  departOn?: number[] | null;
  minNights?: number | null;
  maxNights?: number | null;
  channels?: string[] | null;
  /**
   * `block` refuses; `contact` keeps the stay sellable but not automatically;
   * `indicative` shows the price and says it is not a quote.
   */
  effect?: 'block' | 'contact' | 'indicative';
  /** What the GUEST is told. Read on the site, so it is the park's own wording. */
  label?: string | null;
  /** Internal. Never shown to a guest. */
  note?: string | null;
}

/** What `/api/park-settings` answers with. Every field optional; absent means default. */
export interface MeDto {
  operatorId: string;
  parks: string[];
  expiresAt: string;
  login: string | null;
  label: string | null;
  /** What this account may do. See `api.me`. */
  capabilities: string[];
}

export interface OperatorDto {
  id: string;
  login: string;
  label: string;
  parks: string[];
  capabilities: string[];
  disabledAt: string | null;
}

export interface ParkSettingsDto {
  defaultCountry?: string;
  inquiryEmail?: string;
  defaultLocale?: string;
  currency?: string;
  dateFormat?: 'locale' | 'dmy' | 'mdy' | 'iso';
  timeFormat?: '24h' | '12h';
  /** IANA zone. Absent means UTC — see `lib/format.ts` for why not the viewer's. */
  timeZone?: string;
  /**
   * How soon the park usually answers an enquiry, in hours.
   *
   * Absent means no promise is made, and the guest is simply told a person
   * will reply. A number rather than a sentence so it survives translation —
   * a Dutch park typing one sentence would send it to its English guests.
   */
  replyWithinHours?: number;
}

export const api = {
  health: () => call<{ ok: boolean; workers: number }>('GET', '/api/health'),

  /**
   * `season` asks which card is live for that season rather than the earliest.
   *
   * The list said "2026 live" whatever season was selected, because without one
   * the API answers with the first published version — and on a park where two
   * seasons are published at once, that is the wrong one half the time.
   */
  accommodations: (season?: number) =>
    call<{ accommodations: AccommodationDto[] }>(
      'GET', `/api/accommodations${season ? `?season=${season}` : ''}`,
    ).then((r) => r.accommodations),

  /**
   * Create or change an accommodation.
   *
   * The whole record every time, deliberately: the API's PUT is a replace, and
   * it stores `attributes` as one column — so a call that leaves them out
   * NULLS them. Renaming a pitch type would otherwise silently forget its
   * surface area and its power supply.
   */
  putAccommodation: (id: string, body: {
    label: string; kind?: string;
    attributes?: Record<string, string | number | boolean>;
  }) => call<AccommodationDto>('PUT', `/api/accommodations/${encodeURIComponent(id)}`, {
    kind: 'custom', ...body,
  }),

  attributes: () =>
    call<{ attributes: AttributeDefDto[] }>('GET', '/api/attributes').then((r) => r.attributes),

  /**
   * Who is signed in — and, when nobody is, whether there is anybody to sign in as.
   *
   * `setupRequired` is true on a park that has just been deployed and has no
   * operator yet. A login form there is a dead end.
   */
  /**
   * Who is signed in, and WHAT THEY MAY DO.
   *
   * The capabilities are the panel's copy of a decision the server has already
   * made and will make again on every request. They exist so the panel can
   * decline to offer what it knows will be refused — a 403 after a button
   * press is a correct answer and a terrible interface. They are not the
   * enforcement and must never be treated as it.
   */
  me: () => call<MeDto>('GET', '/api/me'),

  operators: () => call<{ operators: OperatorDto[]; capabilities: string[] }>(
    'GET', '/api/operators',
  ),

  createOperator: (body: {
    login: string; password: string; label?: string; capabilities: string[];
  }) => call<OperatorDto>('POST', '/api/operators', body),

  updateOperator: (id: string, body: {
    capabilities?: string[]; label?: string; password?: string; disabled?: boolean;
  }) => call<OperatorDto>('PATCH', `/api/operators/${encodeURIComponent(id)}`, body),

  /** The parks this server serves. Used for the name in the header. */
  parks: () => call<{ parks: Array<{ id: string; label: string }> }>('GET', '/api/parks')
    .then((r) => r.parks),

  login: (login: string, password: string) =>
    call<{ operatorId: string; label: string; parks: string[] }>(
      'POST', '/api/login', { login, password },
    ),

  logout: () => call<{ ok: boolean }>('POST', '/api/logout'),

  /**
   * Change your own password. Ends every other session, keeps this one.
   */
  changePassword: (current: string, next: string) =>
    call<{ ok: boolean }>('POST', '/api/password', { current, next }),

  /** The first account, once. The route refuses the moment an operator exists. */
  setup: (login: string, password: string, label?: string) =>
    call<{ id: string; login: string }>('POST', '/api/setup', { login, password, label }),

  stayRules: (accommodation?: string) =>
    call<{ rules: StayRuleDto[] }>(
      'GET',
      `/api/stay-rules${accommodation ? `?accommodation=${encodeURIComponent(accommodation)}` : ''}`,
    ).then((r) => r.rules),

  putStayRule: (id: string, rule: Omit<StayRuleDto, 'id'>) =>
    call<StayRuleDto>('PUT', `/api/stay-rules/${encodeURIComponent(id)}`, rule),

  deleteStayRule: (id: string) =>
    call<{ deleted: string }>('DELETE', `/api/stay-rules/${encodeURIComponent(id)}`),

  deleteAccommodation: (id: string) =>
    call<{ deleted: string }>('DELETE', `/api/accommodations/${encodeURIComponent(id)}`),

  versions: (accommodationId?: string) =>
    call<{ versions: VersionSummaryDto[] }>(
      'GET',
      `/api/versions${accommodationId ? `?accommodation=${encodeURIComponent(accommodationId)}` : ''}`,
    ).then((r) => r.versions),

  version: (id: string) => call<VersionDto>('GET', `/api/versions/${encodeURIComponent(id)}`),

  createVersion: (v: {
    id: string; accommodationId: string; label: string; seasonYear: number; programme?: Programme;
  }) => call<VersionSummaryDto>('POST', '/api/versions', v),

  updateVersion: (id: string, patch: { programme?: Programme; label?: string }) =>
    call<VersionSummaryDto>('PUT', `/api/versions/${encodeURIComponent(id)}`, patch),

  cloneVersion: (id: string, opts: {
    id: string; label: string; accommodationId?: string; seasonYear?: number;
    shiftDays?: number; dropRoles?: string[];
  }) => call<VersionSummaryDto>('POST', `/api/versions/${encodeURIComponent(id)}/clone`, opts),

  /**
   * Take a version live, optionally for a stated window of arrivals.
   *
   * Both ends or neither — the API rejects half a window rather than guessing
   * the other end. `live` comes back listing every table still live for the
   * accommodation afterwards, which is the only answer to "is this the one
   * guests are quoted from" that survives a second season being on sale.
   */
  publishVersion: (id: string, window?: { from: string; until: string }) =>
    call<{
      published: VersionSummaryDto;
      replaced: { id: string; label: string } | null;
      replacedAll?: string[];
      live?: Array<{ id: string; validFrom: string | null; validUntil: string | null }>;
      warnings: Diagnostic[];
    }>('POST', `/api/versions/${encodeURIComponent(id)}/publish`, window ?? {}),

  /**
   * Which version prices a stay arriving on a given day.
   *
   * There is no route that answers this directly, so it is asked of the quote
   * route — which resolves the arrival to a live table exactly as a guest's
   * quote would, and names it. One night and an empty party: the figure is not
   * wanted, the provenance is. A 404 here is the real answer "nothing live
   * covers that date", and it arrives with the API's own sentence.
   */
  versionForArrival: (accommodationId: string, arrival: string) =>
    call<{ versionId: string; accommodationId: string; state: VersionState; arrival: string }>(
      'POST', '/api/quote', { accommodationId, arrival, nights: 1 },
    ),

  setVersionState: (id: string, state: 'draft' | 'archived') =>
    call<VersionSummaryDto>('POST', `/api/versions/${encodeURIComponent(id)}/state`, { state }),

  deleteVersion: (id: string) =>
    call<{ deleted: string }>('DELETE', `/api/versions/${encodeURIComponent(id)}`),

  diffVersions: (id: string, against: string) =>
    call<{ from: string; to: string; diff: ProgrammeDiff }>(
      'GET',
      `/api/versions/${encodeURIComponent(id)}/diff?against=${encodeURIComponent(against)}`,
    ),

  copyRates: (id: string, from: string, rateIds: string[]) =>
    call<VersionSummaryDto>('POST', `/api/versions/${encodeURIComponent(id)}/copy-rates`,
      { from, rateIds }),

  proposeLadder: (id: string, steps: LadderStep[], opts: {
    requireWholeBand?: boolean; roundToEuro?: boolean;
  } = {}) => call<LadderProposalDto>(
    'POST', `/api/versions/${encodeURIComponent(id)}/propose-ladder`, { steps, ...opts },
  ),

  /**
   * `override` names hand-edited rungs to regenerate anyway. Anything the server
   * reports as protected and this list does not name is KEPT, and comes back in
   * `kept` — so a blanket accept can never revert a price somebody typed.
   */
  applyLadder: (id: string, steps: LadderStep[], accept: string[], opts: {
    requireWholeBand?: boolean; roundToEuro?: boolean; override?: string[];
  } = {}) => call<{
    version: VersionSummaryDto; applied: number; kept?: string[]; diagnostics: Diagnostic[];
  }>(
    'POST', `/api/versions/${encodeURIComponent(id)}/apply-ladder`, { steps, accept, ...opts },
  ),

  /**
   * How this park writes dates, times and money, plus where enquiries go.
   *
   * Display only, all of it. Nothing here changes what is stored or transmitted:
   * that is ISO 8601 and UTC and is not a setting.
   */
  parkSettings: () => call<ParkSettingsDto>('GET', '/api/park-settings'),

  putParkSettings: (patch: Partial<ParkSettingsDto>) =>
    call<ParkSettingsDto>('PUT', '/api/park-settings', patch),

  periods: (year?: number) =>
    call<{ periods: Period[] }>('GET', `/api/periods${year ? `?year=${year}` : ''}`)
      .then((r) => r.periods),

  putPeriod: (p: Period) =>
    call<Period>('PUT', `/api/periods/${encodeURIComponent(p.id)}`, p),

  deletePeriod: (id: string) =>
    call<{ deleted: string }>('DELETE', `/api/periods/${encodeURIComponent(id)}`),

  seedPeriods: (year: number) =>
    call<{ year: number; added: number; periods: Period[] }>('POST', '/api/periods/seed', { year }),

  publish: (body: {
    versionId?: string; accommodationId?: string; from: string; days: number;
    lengths?: number[]; quantities?: Record<string, number>;
  }) => call<{ jobId: string; cells: number; poll: string }>('POST', '/api/publish', body),

  job: (id: string) => call<{
    id: string; state: 'running' | 'done' | 'failed'; done: number; total: number;
    result?: { cells: number; bands: number; stats: Record<string, number> }; error?: string;
  }>('GET', `/api/jobs/${encodeURIComponent(id)}`),

  /* ------------------------------------------------------------------ units */

  units: (accommodationId?: string) =>
    call<{ units: UnitDto[] }>(
      'GET',
      `/api/units${accommodationId ? `?accommodation=${encodeURIComponent(accommodationId)}` : ''}`,
    ).then((r) => r.units),

  unitGroups: () =>
    call<{ groups: UnitGroupDto[] }>('GET', '/api/unit-groups').then((r) => r.groups),

  putUnitGroup: (g: UnitGroupDto) =>
    call<UnitGroupDto>('PUT', `/api/unit-groups/${encodeURIComponent(g.id)}`,
      { label: g.label, sort: g.sort ?? 0 }),

  putUnit: (u: UnitDto) =>
    call<UnitDto>('PUT', `/api/units/${encodeURIComponent(u.id)}`, {
      accommodationId: u.accommodationId,
      label: u.label,
      address: u.address ?? undefined,
      groupId: u.groupId ?? undefined,
      number: u.number ?? undefined,
      sort: u.sort ?? 0,
      active: u.active !== false,
      selectable: u.selectable === true,
      note: u.note ?? undefined,
    }),

  deleteUnit: (id: string) =>
    call<{ deleted: string }>('DELETE', `/api/units/${encodeURIComponent(id)}`),

  /** A numbered range in one go: "Berkenlaan is 1 to 47". */
  generateUnits: (body: {
    accommodationId: string; groupId?: string; prefix?: string;
    from: number; to: number; idPrefix?: string;
  }) => call<{ created: number; units: UnitDto[]; alreadyTaken: string[] }>(
    'POST', '/api/units/generate', body,
  ),

  blocks: (accommodationId?: string, window?: { from: string; until: string }) => {
    const q = new URLSearchParams();
    if (accommodationId) q.set('accommodation', accommodationId);
    if (window) { q.set('from', window.from); q.set('until', window.until); }
    const qs = q.toString();
    return call<{ blocks: BlockDto[] }>('GET', `/api/blocks${qs ? `?${qs}` : ''}`)
      .then((r) => r.blocks);
  },

  putBlock: (b: BlockDto) =>
    call<BlockDto>('PUT', `/api/blocks/${encodeURIComponent(b.id)}`, {
      unitId: b.unitId, from: b.from, until: b.until, reason: b.reason, note: b.note ?? undefined,
    }),

  deleteBlock: (id: string) =>
    call<{ deleted: string }>('DELETE', `/api/blocks/${encodeURIComponent(id)}`),

  /* -------------------------------------------------------------- enquiries */

  inquiries: (status?: string) =>
    call<{ inquiries: InquiryDto[] }>(
      'GET', `/api/inquiries${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ).then((r) => r.inquiries),

  /**
   * Change an enquiry's status.
   *
   * A POST to a sub-path, not a PUT to the enquiry: the record itself is what a
   * guest wrote and is never edited here. Nothing about the person goes in the
   * URL — only the enquiry's own id.
   */
  setInquiryStatus: (id: string, status: string) =>
    call<InquiryDto>('POST', `/api/inquiries/${encodeURIComponent(id)}/status`, { status }),

  /**
   * The mails the park sent about one enquiry, as they were sent.
   *
   * Kept rather than regenerated: the tariff moves, the templates move, and
   * the park's language setting moves. When a guest says "your mail said X",
   * the useful answer is the bytes that left the building.
   */
  inquiryMails: (id: string) =>
    call<{ mails: InquiryMailDto[] }>(
      'GET', `/api/inquiries/${encodeURIComponent(id)}/mails`,
    ),

  rates: (unitType: string, from: string, until: string, los?: number) =>
    call<{ count: number; rates: Array<{ date: string; los: number; price: number | null;
      status: string; source: string }> }>(
      'GET',
      `/api/rates/${encodeURIComponent(unitType)}?from=${from}&until=${until}`
        + (los ? `&los=${los}` : ''),
    ),
};
